import { useSuspenseQuery } from '@apollo/client';
import getInitials from 'initials';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import uniqolor from 'uniqolor';

import {
  Avatar,
  AvatarFallback,
  Badge,
  EluveLogo,
  Icon,
  Input,
  SimpleTooltip,
  Switch,
  Text,
  cn,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

export const getTenantTemplateBundlesListQuery = graphql(`
  query getTenantTemplateBundlesList($tenantId: uuid!, $userId: uuid!) {
    tenantTemplateBundles(
      where: {
        tenantId: { _eq: $tenantId }
        llm_model_prompt_template: {
          isActive: { _eq: true }
          # Surface a bundle once it has a version this user can open: either a
          # published variant, or a draft they authored themselves. This keeps
          # a colleague's draft-only template hidden (others can't read another
          # user's draft) while still letting an author return to the draft they
          # just saved to publish it.
          output_template: {
            variants: {
              _or: [
                { isDraft: { _eq: false } }
                { createdBy: { _eq: $userId } }
              ]
            }
          }
        }
      }
    ) {
      __typename
      tenantId
      llmModelPromptTemplateId
      isEnabled
      createdAt
      llm_model_prompt_template {
        __typename
        id
        displayName
        output_template {
          __typename
          id
          tenantId
          name
          description
          variants(orderBy: { variantNumber: ASC }, limit: 1) {
            __typename
            outputTemplateId
            variantNumber
            creator {
              __typename
              id
              firstName
              lastName
              isEluveAdmin
            }
          }
          # Pending (unpublished) drafts the current user can open. Row-level
          # permissions only return a user's OWN drafts, so this surfaces
          # "you have unsaved work here" without exposing colleagues' drafts.
          draftVariants: variants(where: { isDraft: { _eq: true } }, limit: 1) {
            __typename
            outputTemplateId
            variantNumber
          }
          # The currently-active (published, default) version, if any. Used to
          # show the live version number and to suppress the Draft badge once a
          # template has been published.
          activeVariants: variants(
            where: { isCurrentDefault: { _eq: true } }
            limit: 1
          ) {
            __typename
            outputTemplateId
            variantNumber
          }
        }
      }
    }
  }
`);

type TemplateBundleCard = {
  id: string;
  outputTemplateId: string | null;
  name: string;
  description: string | null;
  isEnabled: boolean;
  /**
   * Global (Eluve-provided) templates have no tenant owner, so the
   * tenant-facing save/publish path rejects them ("Template not found").
   * They're surfaced as read-only rather than opened into an editor that
   * can't save.
   */
  isReadOnly: boolean;
  /** The current user has an unpublished draft version of this template. */
  hasDraft: boolean;
  /** Variant number of the active (published, default) version, if published. */
  activeVersionNumber: number | null;
  createdAt: string;
  /**
   * Creator of the template's first version. Null when the creator
   * isn't visible to this tenant (e.g. an Eluve admin), which we
   * attribute to Eluve.
   */
  creator: {
    id: string;
    name: string;
    firstName: string;
    isEluveAdmin: boolean;
  } | null;
};

// Private Components

const CreatorChip: React.FC<{ creator: TemplateBundleCard['creator'] }> = ({
  creator,
}) => {
  if (!creator || creator.isEluveAdmin) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex size-6 items-center justify-center rounded-full bg-bg-secondary">
          <EluveLogo className="size-3.5 fill-text-primary" />
        </div>
        <Text size="xs" color="tertiary">
          Eluve
        </Text>
      </div>
    );
  }

  const bgColor = uniqolor(creator.id, { lightness: [50, 85] });
  return (
    <div className="flex items-center gap-2">
      <Avatar size="sm">
        <AvatarFallback style={{ backgroundColor: bgColor.color }}>
          <span
            className={cn(
              'text-[10px] font-semibold',
              bgColor.isLight
                ? 'text-text-primary/60 mix-blend-color-burn'
                : 'text-bg-primary mix-blend-overlay',
            )}
          >
            {getInitials(creator.name).slice(0, 2)}
          </span>
        </AvatarFallback>
      </Avatar>
      <Text size="xs" color="tertiary" className="privacy-text">
        {creator.firstName}
      </Text>
    </div>
  );
};

const TemplateCard: React.FC<{ card: TemplateBundleCard }> = ({ card }) => {
  const { t } = useTranslation();
  const body = (
    <>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <Text weight="semibold" className="leading-snug">
            {card.name}
          </Text>
          <div className="flex shrink-0 items-center gap-1.5">
            {card.activeVersionNumber == null && card.hasDraft && (
              <SimpleTooltip
                label={t('outputTemplates.bundlesList.draftTooltip')}
              >
                <span className="inline-flex">
                  <Badge variant="secondary" className="gap-1">
                    <Icon name="FileEdit" size="xs" />
                    {t('outputTemplates.bundlesList.draft')}
                  </Badge>
                </span>
              </SimpleTooltip>
            )}
            {card.isReadOnly && (
              <SimpleTooltip
                label={t('outputTemplates.bundlesList.readOnlyTooltip')}
              >
                <span className="inline-flex">
                  <Badge variant="outline" className="gap-1">
                    <Icon name="Lock" size="xs" />
                    {t('outputTemplates.bundlesList.readOnly')}
                  </Badge>
                </span>
              </SimpleTooltip>
            )}
            {!card.isEnabled && (
              <SimpleTooltip
                label={t('outputTemplates.bundlesList.disabledTooltip')}
              >
                <span className="inline-flex">
                  <Badge variant="outline">
                    {t('outputTemplates.bundlesList.disabled')}
                  </Badge>
                </span>
              </SimpleTooltip>
            )}
          </div>
        </div>
        <Text size="sm" color="tertiary" className="line-clamp-2 min-h-10">
          {card.description ?? t('outputTemplates.bundlesList.noDescription')}
        </Text>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border-primary px-4 py-3">
        <CreatorChip creator={card.creator} />
        <Text size="xs" color="tertiary">
          {new Date(card.createdAt).toLocaleDateString()}
          {card.activeVersionNumber != null && (
            <>
              {' · '}
              <span className="font-mono">V{card.activeVersionNumber}</span>
            </>
          )}
        </Text>
      </div>
    </>
  );

  const className =
    'flex h-full flex-col rounded-xl border border-border-primary bg-bg-primary text-left transition-all duration-200';

  if (!card.outputTemplateId) {
    return <div className={className}>{body}</div>;
  }

  return (
    <Link
      to={`bundles/${card.outputTemplateId}`}
      className={cn(
        className,
        'hover:border-border-tertiary hover:shadow-md focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
      )}
    >
      {body}
    </Link>
  );
};

export interface TenantTemplateBundlesListProps {}

/**
 * Card grid of the template bundles assigned to the current tenant:
 * title, description, enabled state, and creator attribution — the
 * Eluve mark for Eluve-authored templates, the creator's avatar and
 * first name for templates made by someone in the practice.
 */
export const TenantTemplateBundlesList: React.FC<
  TenantTemplateBundlesListProps
> = () => {
  const { t } = useTranslation();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();
  const [search, setSearch] = useState('');
  const [showOnlyMine, setShowOnlyMine] = useState(false);

  const { data } = useSuspenseQuery(getTenantTemplateBundlesListQuery, {
    variables: {
      tenantId: tenantId!,
      userId: userId!,
    },
  });

  const cards = useMemo(() => {
    return data.tenantTemplateBundles
      .map<TemplateBundleCard>((assignment) => {
        const bundle = assignment.llm_model_prompt_template;
        const outputTemplate = bundle.output_template;
        const creator = outputTemplate?.variants?.[0]?.creator ?? null;
        return {
          id: assignment.llmModelPromptTemplateId,
          outputTemplateId: outputTemplate?.id ?? null,
          name: bundle.displayName ?? outputTemplate?.name ?? '',
          description: outputTemplate?.description ?? null,
          isEnabled: assignment.isEnabled,
          // Editable only when this tenant owns the underlying template;
          // global/Eluve templates (no tenant) can't be saved back.
          isReadOnly: (outputTemplate?.tenantId ?? null) !== tenantId,
          hasDraft: (outputTemplate?.draftVariants?.length ?? 0) > 0,
          activeVersionNumber:
            outputTemplate?.activeVariants?.[0]?.variantNumber != null
              ? Number(outputTemplate.activeVariants[0].variantNumber)
              : null,
          createdAt: assignment.createdAt,
          creator: creator
            ? {
                id: creator.id,
                name: [creator.firstName, creator.lastName]
                  .filter(Boolean)
                  .join(' '),
                firstName: creator.firstName ?? '',
                isEluveAdmin: creator.isEluveAdmin ?? false,
              }
            : null,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, tenantId]);

  // "System" templates are the read-only Eluve-provided ones (no tenant owner);
  // the rest are this organization's own. The ownership filter is only useful
  // when the tenant has both — otherwise it would just empty the list.
  const canFilterOwnership = useMemo(
    () =>
      cards.some((card) => card.isReadOnly) &&
      cards.some((card) => !card.isReadOnly),
    [cards],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return cards.filter((card) => {
      if (showOnlyMine && card.isReadOnly) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [card.name, card.description ?? '', card.creator?.name ?? 'Eluve']
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [cards, search, showOnlyMine]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Icon
            name="Search"
            size="sm"
            className="absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('outputTemplates.bundlesList.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        {canFilterOwnership && (
          <label className="flex cursor-pointer items-center gap-2">
            <Switch
              checked={showOnlyMine}
              onCheckedChange={setShowOnlyMine}
              aria-label={t('outputTemplates.bundlesList.showOnlyMine')}
            />
            <Text size="sm" color="secondary">
              {t('outputTemplates.bundlesList.showOnlyMine')}
            </Text>
          </label>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border-primary py-16">
          <Icon
            name="LayoutTemplate"
            size="xl"
            className="text-text-tertiary"
          />
          <Text color="tertiary" size="sm">
            {cards.length === 0
              ? t('outputTemplates.bundlesList.emptyState')
              : t('outputTemplates.bundlesList.noMatches')}
          </Text>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((card) => (
            <TemplateCard key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
};
